import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, MapPin, Plane, Hotel as HotelIcon, X } from 'lucide-react';

/**
 * Searchable input where user can type freely.
 * Shows filtered suggestions (airports + hotels) below.
 * Allows custom values typed by the user.
 *
 * Props:
 * - value: string
 * - onChange: (string) => void
 * - airports: [{name, code, city, type}]
 * - hotels: [{name, zone}]
 * - placeholder: string
 */
const SearchableLocation = ({ value, onChange, airports = [], hotels = [], placeholder = 'Buscar...' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const wrapRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filteredAirports = useMemo(() => {
    if (!query) return airports.slice(0, 8);
    const q = query.toLowerCase();
    return airports.filter(a =>
      a.name.toLowerCase().includes(q) || (a.code || '').toLowerCase().includes(q) || (a.city || '').toLowerCase().includes(q)
    ).slice(0, 8);
  }, [query, airports]);

  const filteredHotels = useMemo(() => {
    if (!query) return hotels.slice(0, 10);
    const q = query.toLowerCase();
    return hotels.filter(h =>
      h.name.toLowerCase().includes(q) || (h.zone || '').toLowerCase().includes(q)
    ).slice(0, 12);
  }, [query, hotels]);

  const select = (text) => {
    setQuery(text);
    onChange(text);
    setOpen(false);
  };

  const clear = () => {
    setQuery('');
    onChange('');
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full h-11 bg-black/40 border border-white/10 text-white text-sm rounded-md pl-9 pr-9 outline-none focus:border-[#ff6b1a] transition-colors"
        />
        {query && (
          <button type="button" onClick={clear} className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded hover:bg-white/10 flex items-center justify-center text-white/50">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && (filteredAirports.length > 0 || filteredHotels.length > 0 || query) && (
        <div className="absolute z-50 left-0 right-0 mt-2 bg-[#0f0f0f] border border-white/10 rounded-lg shadow-2xl max-h-72 overflow-y-auto no-scrollbar">
          {filteredAirports.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#ff6b1a] font-bold border-b border-white/5 bg-black/40">
                Aeropuertos & Puertos
              </div>
              {filteredAirports.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => select(a.name)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm text-white/85 hover:bg-[#ff6b1a]/15 hover:text-white transition-colors"
                >
                  <Plane className="w-4 h-4 text-[#ff6b1a] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{a.name}</div>
                    <div className="text-[10px] text-white/45">{a.code} • {a.city}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {filteredHotels.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#ff6b1a] font-bold border-b border-white/5 bg-black/40">
                Hoteles
              </div>
              {filteredHotels.map((h) => (
                <button
                  key={h.id}
                  type="button"
                  onClick={() => select(h.name)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm text-white/85 hover:bg-[#ff6b1a]/15 hover:text-white transition-colors"
                >
                  <HotelIcon className="w-4 h-4 text-[#ff6b1a] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{h.name}</div>
                    <div className="text-[10px] text-white/45">{h.zone}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {query && filteredAirports.length === 0 && filteredHotels.length === 0 && (
            <button
              type="button"
              onClick={() => select(query)}
              className="w-full flex items-center gap-3 px-3 py-3 text-left text-sm text-white/85 hover:bg-[#ff6b1a]/15"
            >
              <MapPin className="w-4 h-4 text-[#ff6b1a]" />
              <span>Usar: <strong className="text-white">{query}</strong></span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableLocation;
